import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import "./CSS/Shopcategory.css";
import { useProductsByCategory } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import Item from '../componnents/Item/item';
import { LoadingSpinner, ProductSkeleton } from '../componnents/UI';
import {
  filterListableProducts,
  getProductImageUrl,
  getProductId,
  getProductName,
  getProductPrices,
  isProductInStock,
} from '../utils/helpers';
import { Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { translateProducts } from '../utils/translateData';

const Shopcategory = ({ banner, category }) => {
  const { t } = useTranslation(['search', 'common']);
  const { data: categories } = useCategories();
  
  const categoryData = categories?.find(
    cat => cat.name?.toLowerCase() === category?.toLowerCase() ||
           cat.slug?.toLowerCase() === category?.toLowerCase()
  );
  
  const { data, isLoading, error } = useProductsByCategory(categoryData?.id || categoryData?._id, { limit: 20, page: 1 });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  
  // Update products when data changes
  useEffect(() => {
    if (data) {
      const newProducts = filterListableProducts(data.products || []);
      if (currentPage === 1) {
        setAllProducts(newProducts);
      }
      
      // Check if there are more products
      const pagination = data.pagination;
      const total = data.total || 0;
      const currentTotal = currentPage === 1 ? newProducts.length : allProducts.length + newProducts.length;
      setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  
  const products = filterListableProducts(
    allProducts.length > 0 ? allProducts : (data?.products || [])
  );

  return (
    <div className='shop-category'>
      <img src={banner} alt="Category-Banner" className="Category-Banner" />


      {isLoading && (
        <div className="shopcategory-loading">
          <LoadingSpinner text={t('loadingProducts', { defaultValue: 'Loading products...', ns: 'search' })} size="medium" />
        </div>
      )}

      {error && (
        <div className="shopcategory-error">
          <p>{t('failedToLoad', { ns: 'search' })}</p>
        </div>
      )}

      <div className='shopcategory-products'>
        {isLoading ? (
          <ProductSkeleton count={8} />
        ) : (
          products.map((item, i) => {
            const { price, comparePrice, fromPrice } = getProductPrices(item);
            return (
              <Item
                key={getProductId(item) || i}
                id={getProductId(item)}
                image={getProductImageUrl(item)}
                name={getProductName(item)}
                newPrice={price}
                oldPrice={comparePrice}
                fromPrice={fromPrice}
                sold={item.sold || item.salesCount || 0}
                rating={item.averageRating || item.rating || 4.5}
                shipping={item.freeShipping ? t('freeShipping') : t('standardShipping')}
                inStock={isProductInStock(item)}
              />
            );
          })
        )}
      </div>

      {!isLoading && products.length === 0 && (
        <div className="shopcategory-empty">
          <p>{t('noProductsInCategory', { ns: 'search' })}</p>
        </div>
      )}

      {hasMore && products.length > 0 && (
        <div className="shopcategory-loadmore" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <button
            onClick={async () => {
              setIsLoadingMore(true);
              try {
                const nextPage = currentPage + 1;
                const response = await apiService.getProductsByCategory(categoryData?.id || categoryData?._id, { limit: 20, page: nextPage });
                const newProducts = filterListableProducts(
                  translateProducts(response.products || [])
                );
                setAllProducts(prev => [...prev, ...newProducts]);
                setCurrentPage(nextPage);
                
                // Check if there are more products
                const pagination = response.pagination;
                const total = response.total || 0;
                const currentTotal = allProducts.length + newProducts.length;
                setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
              } catch (err) {
                console.error('Failed to load more products:', err);
                setHasMore(false);
              } finally {
                setIsLoadingMore(false);
              }
            }}
            disabled={isLoadingMore}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 500,
              color: '#fff',
              backgroundColor: '#2563eb',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: isLoadingMore ? 'not-allowed' : 'pointer',
              opacity: isLoadingMore ? 0.7 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isLoadingMore ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t('loadingMore', { ns: 'common' })}
              </>
            ) : (
              t('loadMore', { ns: 'common' })
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Shopcategory;
